import {PI_CARDINAL} from '../pi/piPaths';
import type {PlushState} from './PaiPlush';
import {PlushFace, PlushFrame} from './plushKit';

/**
 * Marbles: a pink axolotl plush hugging a glass jar of colored marbles —
 * the classic "draw a marble from an urn" problem. Frilly magenta gills on
 * each side of its head, a wide smile, a little tail, and stubby hands.
 */

const FUR = {light: '#FFDCE5', mid: '#F7B2C4', dark: '#E48BA5'};
const GILL = '#E8628E';
const HAND = '#F29BB3';
const GLASS = '#DCEFF7';
const GLASS_EDGE = '#A9CFE0';
const LID = '#C9A77C';

/** Marbles in the jar: position and color. */
const MARBLES = [
  {cx: 88, cy: 176, color: PI_CARDINAL},
  {cx: 104, cy: 178, color: '#4F86C6'},
  {cx: 118, cy: 175, color: PI_CARDINAL},
  {cx: 95, cy: 162, color: '#E0A030'},
  {cx: 111, cy: 162, color: PI_CARDINAL},
  {cx: 103, cy: 149, color: '#4F86C6'},
] as const;

/** Gill stalks: start, tip, and tip-bulb radius, left side (mirrored for the right). */
const GILLS = [
  {d: 'M 36 84 C 20 76 10 66 8 54', tip: {x: 8, y: 54}, r: 6},
  {d: 'M 32 98 C 16 96 6 90 0 82', tip: {x: 0, y: 82}, r: 5.5},
  {d: 'M 34 112 C 20 116 8 116 0 112', tip: {x: 0, y: 112}, r: 5},
] as const;

/** Props for {@link MarblesPlush}. */
export interface MarblesPlushProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
}

/**
 * Renders Marbles (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link MarblesPlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function MarblesPlush({size, state = 'idle'}: MarblesPlushProps) {
  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={FUR}
      eyeLineFraction={96 / 220}
      thoughtAnchor={{x: 150, y: 40}}
    >
      {kit => (
        <>
          <g filter={kit.fuzz}>
            <path
              d="M 150 180 C 176 186 194 176 198 160 C 184 168 168 168 156 164 Z"
              fill={kit.fur}
            />
            {[1, -1].flatMap(side =>
              GILLS.map(({d, tip, r}) => (
                <g
                  key={`${side}-${tip.y}`}
                  transform={
                    side === 1 ? undefined : 'translate(200 0) scale(-1 1)'
                  }
                >
                  <path
                    d={d}
                    stroke={GILL}
                    strokeWidth={7}
                    strokeLinecap="round"
                    fill="none"
                  />
                  <circle cx={tip.x} cy={tip.y} r={r} fill={GILL} />
                </g>
              )),
            )}
            <ellipse cx={76} cy={198} rx={17} ry={8} fill={HAND} />
            <ellipse cx={124} cy={198} rx={17} ry={8} fill={HAND} />
            <path
              d="M100 38 C152 38 176 76 176 124 C176 168 146 194 100 194 C54 194 24 168 24 124 C24 76 48 38 100 38 Z"
              fill={kit.fur}
            />
          </g>
          <PlushFace kit={kit} x={100} y={96} scale={1.05} />
          {/* Jar of marbles, hugged in front. */}
          <rect
            x={78}
            y={138}
            width={48}
            height={52}
            rx={12}
            fill={GLASS}
            opacity={0.85}
            stroke={GLASS_EDGE}
            strokeWidth={2}
          />
          {MARBLES.map(({cx, cy, color}) => (
            <g key={`${cx}-${cy}`}>
              <circle cx={cx} cy={cy} r={7} fill={color} />
              <circle
                cx={cx - 2}
                cy={cy - 2.5}
                r={2}
                fill="#fff"
                opacity={0.6}
              />
            </g>
          ))}
          <rect x={82} y={130} width={40} height={10} rx={4} fill={LID} />
          <path
            d="M 84 146 v 30"
            stroke="#fff"
            strokeWidth={3}
            strokeLinecap="round"
            opacity={0.55}
          />
          <g filter={kit.fuzz}>
            <ellipse cx={76} cy={164} rx={10} ry={12} fill={HAND} />
            <ellipse cx={128} cy={160} rx={10} ry={12} fill={HAND} />
          </g>
        </>
      )}
    </PlushFrame>
  );
}
